<template>
  <div class="profile">
    <div class="profile__head">
      <div class="profile__elements">
        <div
          class="profile__button"
          role="button"
          @click="showCloseConversationModal('cancel')"
          v-tooltip="$t('cancel-modal.title')"
        >
          <chat-cancel-icon class="profile__button-icon" />
        </div>
        <div
          v-if="enableTransfer"
          class="profile__button"
          role="button"
          @click="transferConversation"
          v-tooltip="$t('transfer-conversation.title')"
        >
          <chat-transfer-icon
            class="profile__button-icon profile__button-transfer"
          />
        </div>
        <div
          class="profile__button"
          role="button"
          @click="showCloseConversationModal('finish')"
          v-tooltip="$t('cancel-modal.finish')"
        >
          <chat-finish-icon class="profile__button-icon" />
        </div>
      </div>
    </div>

    <div class="profile__body">
      <div class="profile__main">
        <user-profile
          :name="clientName"
          :picture="profileUrl"
          class="profile__main-image"
        />

        <div class="profile__main-title">
          <span v-if="!editName" @click="changEditName" role="button">
            {{ clientName }}
          </span>
          <span v-else class="profile__main-title-input">
            <hcc-input
              class="modal-input"
              v-model.trim="clientNameInput"
              @keyup.enter="submitName"
            />
            <cancel-icon
              role="button"
              class="profile__main-title-icon red"
              @click="cancelEditName"
            />
            <check-single-icon
              role="button"
              class="profile__main-title-icon green"
              @click="submitName"
            />
          </span>
        </div>
        <div v-if="!hideUsername" class="profile__main-subtitle">
          {{ clientUsername }}
        </div>
        <div class="profile__main-campaign">
          {{ campaignName }}
        </div>

        <div class="profile__main-subtitle">
          <channel-badge :type="channelType" class="profile__main-channel" />
          <div>{{ channelName }}</div>
        </div>

        <div
          v-if="isWhatsAppChannel"
          class="profile__main-time"
          :class="isWarning"
        >
          <div v-if="hasExpired">{{ $t("chat.has-expired") }}</div>
          <div v-else>
            {{ $t("chat.expired-whatsapp") }}
            <span>{{ expiredTime() }}</span>
          </div>
        </div>
      </div>

      <div class="profile__scrollable">
        <vue-scroll>
          <div class="profile__block profile__block--add-border">
            <div
              class="profile__action profile__action—pull-left"
              @click="openMultimedia"
              role="button"
            >
              <div class="profile__action-icon">
                <database-icon />
              </div>
              <div class="profile__action-text">
                {{ $t("multimedia.name") }}
              </div>
            </div>
          </div>

          <labels
            :campaignId="campaignId"
            :clientId="clientId"
            :userLabels="userLabels || []"
          />

          <div class="profile__block">
            <div
              @click="toggleHistory"
              :class="{ 'is-opened': showHistory }"
              class="profile__action profile__action--toggle"
              role="button"
            >
              <div class="profile__action-icon">
                <ellipsis-icon />
              </div>
              <div class="profile__action-text">
                {{ $t("chat.last-conversation") }}
              </div>
            </div>
            <template v-if="showHistory">
              <div class="profile__history">
                <div v-if="!hasHistory" class="profile__no-content">
                  {{ $t("chat.no-history") }}
                </div>
                <profile-history-item
                  v-else
                  v-for="(h, index) in history"
                  :key="`${clientId}-${index}`"
                  :history="h"
                />
              </div>
            </template>
          </div>
        </vue-scroll>
      </div>
    </div>

    <transfer-conversation-modal
      v-if="enableTransfer"
      :request="requestUsers"
      @confirm="confirmTransferModal"
      @cancel="cancelTransferModal"
      :campaign-id="campaignId"
    />
    <close-conversation-modal
      :campaign-id="campaignId"
      :conversation-id="conversation.id"
      :action="closeConversationAction"
      @confirm="confirmCloseConversation"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import {
  CONVERSATION_LOAD_HISTORY,
} from '@/eventTypes';
import EventBus from '@/eventBus';
import ChatCancelIcon from '@/assets/icons/chat-cancel.svg';
import ChatFinishIcon from '@/assets/icons/chat-finish.svg';
import ChatTransferIcon from '@/assets/icons/chat-transfer.svg';
import EllipsisIcon from '@/assets/icons/ellipsis.svg';
import saveLabelGql from '@/graphql/mutations/saveLabel.gql';
import actionsconversation from '@/graphql/mutations/actionsconversation.gql';
import CloseConversationModal from '@/components/CloseConversationModal.vue';
import TransferConversationModal from '@/components/TransferConversationModal.vue';
import DatabaseIcon from '@/assets/icons/database.svg';
import UserProfile from '@/components/UserProfile.vue';
import ChannelBadge from '@/components/ChannelBadge.vue';
import errorHandler from '@/utils/error.handler';

export default {
  components: {
    DatabaseIcon,
    UserProfile,
    ChatCancelIcon,
    ChatFinishIcon,
    ChatTransferIcon,
    EllipsisIcon,
    CloseConversationModal,
    TransferConversationModal,
    ChannelBadge,
    ProfileHistoryItem: () => import('@/components/ProfileHistoryItem.vue'),
    Labels: () => import('@/components/labels/index.vue'),
    HccInput: () => import('@/components/shared/HccInput/index.vue'),
    CheckSingleIcon: () => import('@/assets/icons/check-single.svg'),
    CancelIcon: () => import('@/assets/icons/cancel.svg'),
  },
  data() {
    return {
      idClient: '',
      requestUsers: false,
      showHistory: false,
      clientNameInput: null,
      editName: false,
      closeConversationAction: '',
      time: null,
      currentTime: new Date(),
    };
  },
  computed: {
    ...mapGetters([
      'activeConversation',
      'getConfig',
      'userInfo',
    ]),
    client() {
      return this.activeConversation ? this.activeConversation.client ?? {} : {};
    },
    conversation() {
      return this.activeConversation ? this.activeConversation.conversation ?? {} : {};
    },
    lastMessageAt() {
      return this.conversation.lastMessageAt;
    },
    session24h() {
      return this.$moment(this.lastMessageAt).add(24, 'hours');
    },
    duration() {
      return this.$moment.duration(this.session24h.diff(this.currentTime));
    },
    hasExpired() {
      return (this.duration.hours() <= 0 && this.duration.hours() !== 23
      && this.duration.minutes() <= 0 && this.duration.minutes() !== 59)
      || !this.duration.isValid();
    },
    isWarning() {
      return this.duration.hours() <= 1 ? 'warning' : 'normal';
    },
    isWhatsAppChannel() {
      return this.channel.channelType?.key === 'whatsapp';
    },
    campaign() {
      return this.conversation.campaign ?? {};
    },
    channel() {
      return this.conversation.channel ?? {};
    },
    campaignId() {
      return this.campaign.id ?? '';
    },
    channelId() {
      return this.channel.id ?? '';
    },
    clientId() {
      return this.client.id ?? '';
    },
    clientName() {
      return this.client.fullName || this.client.name || this.client.username || '';
    },
    clientUsername() {
      return this.client.username;
    },
    channelName() {
      return this.channel ? this.channel.name
        : '';
    },
    campaignName() {
      return this.campaign ? this.campaign.name
        : '';
    },
    channelType() {
      return this.channel && this.channel.channelType.key ? this.channel.channelType.key : '';
    },
    userLabels() {
      return this.client.labels;
    },
    profileUrl() {
      return this.client.profilePic ?? '';
    },
    history() {
      return this.client.history ?? [];
    },
    historyLoaded() {
      return !!this.client.history;
    },
    hasHistory() {
      return this.history.length > 0;
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    hideUsername() {
      return this.configuration.hideUsername ?? false;
    },
    enableTransfer() {
      return this.configuration.transferConversations ?? false;
    },
  },
  watch: {
    client() {
      if (!this.historyLoaded) {
        EventBus.$emit(CONVERSATION_LOAD_HISTORY, this.clientId);
      }
    },
  },
  mounted() {
    this.time = setInterval(() => {
      this.currentTime = this.$moment();
    }, 60000);
  },
  beforeDestroy() {
    clearInterval(this.time);
  },
  methods: {
    ...mapMutations(['setNameClient', 'addHistoryClient']),
    toggleHistory() {
      this.showHistory = !this.showHistory;
    },
    resetName() {
      this.clientNameInput = null;
    },
    changEditName() {
      this.clientNameInput = this.clientName;
      this.editName = true;
    },
    cancelEditName() {
      this.editName = false;
      this.clientNameInput = null;
    },
    async submitName() {
      if (this.clientNameInput !== null && this.clientNameInput !== ''
        && this.clientNameInput !== this.clientName) {
        const { data: result } = await this.$apollo.mutate({
          mutation: saveLabelGql,
          variables: {
            id: this.clientId,
            name: this.clientNameInput,
          },
        });
        if (result.updateClient) {
          this.cancelEditName();
          this.editName = false;
          this.setNameClient({
            clientId: this.clientId,
            name: result.updateClient.name,
            fullName: result.updateClient.fullName,
          });
        }
      } else {
        this.cancelEditName();
      }
    },
    transferConversation() {
      if (this.enableTransfer) {
        this.requestUsers = true;
        this.$nextTick(() => {
          this.$modal.show('transfer-conversation-modal');
        });
      }
    },
    cancelTransferModal() {
      this.requestUsers = false;
    },
    async confirmTransferModal({ userId, campaignId }) {
      const conversationId = this.activeConversation.conversation.id;
      await this.$apollo.mutate({
        mutation: actionsconversation,
        variables: {
          action: 'TRANSFER',
          conversationId,
          toAgentId: userId,
          campaignId,
        },
      })
        .catch((error) => {
          if (error.graphQLErrors[0].message === 'disconectedUser') {
            this.$toasted.global.error('Error: Agente desconectado!');
          }
        });
    },
    showCloseConversationModal(action) {
      this.closeConversationAction = action;
      this.$modal.show('close-conversation-modal');
    },
    async confirmCloseConversation({ payload, history }) {
      try {
        const dataHistory = history;
        dataHistory.campaign.name = this.activeConversation.conversation.campaign.name;
        dataHistory.agent.name = this.userInfo.name;
        this.addHistoryClient({
          clientId: this.activeConversation.client.id,
          history: dataHistory,
        });
        await this.$apollo.mutate({
          mutation: actionsconversation,
          variables: payload,
        });
      } catch (err) {
        errorHandler.logErrors(err);
        this.$toasted.global.error(`Error: ${err}`);
      }
    },
    openMultimedia() {
      this.$emit('select-client');
    },
    expiredTime() {
      if (this.duration.hours() === 0 && this.duration.minutes() > 0) {
        return `${this.duration.minutes()}m`;
      }
      return `${this.duration.hours()}h ${this.duration.minutes()}m`;
    },
  },
};
</script>
